import { Controller } from "@hotwired/stimulus";
import {Debouncer} from "../utils/debounce.js"

// Fired by Turbo/idiomorph on every element it reconciles during a morph refresh, target = the
// element that was morphed. Bubbles, so listening on the host element and filtering by `target`
// scopes this to the controller's own instance even when several selects share a page.
const MORPH_ELEMENT_EVENT = "turbo:morph-element";

// The row's label text, without its hint: the search and the closed picker use labels only.
function labelOfRow(row: Element): string {
   return (row.querySelector("[data-testid='searchable-select-option-label']") ?? row).textContent!;
}

function rowMatchesSearch(row: Element, query: string, selected: string[]): boolean {
   return selected.includes(row.getAttribute("data-value")!)
      || !query
      || labelOfRow(row).toLowerCase().includes(query.toLowerCase());
}

export default class extends Controller {
   static targets = ["clearButton", "selectedValueText", "dialog", "options", "searchInput", "selectInput", "dropDownButton"]
   static values = {
      selected: {type: Array, default: []},
      selectionMode: {type: String, default: "Single"},
      search: {type: String, default: ""},
      null: {type: String, default: ""},
      placeholder: {type: String, default: ""},
      floating: {type: Boolean, default: false},
      debounceTimeMs: {type: Number, default: 300}
   }

   // ----------
   // Properties
   // ----------
   get isOpen() {
      return this.dialogTarget.getAttribute("open") === ""; //Empty attributes are returned as ''; Non-existing attributes as undefined.
   }

   // Every option row of the control, hidden ones included. Read from the DOM on each call: the
   // page is the only list of options, so a Turbo Stream or morph that edits it needs no repair.
   get optionElements(): Element[] {
      return Array.from(this.element.querySelectorAll("li"));
   }

   // The rows of the options list a search leaves in view.
   get visibleOptionElements(): Element[] {
      return Array.from(this.optionsTarget.children).filter((row) => !row.hasAttribute("hidden"));
   }

   // --------------------------
   // Stimulus Lifecycle methods
   // --------------------------
   initialize() {
      // Own debouncer instance: a table search elsewhere on the page must not cancel this select's
      // pending filter, and vice versa.
      this.searchDebouncer = new Debouncer();
   }

   // Morph-aware: a Turbo morph refresh (e.g. a refused form save posting back to its own URL)
   // keeps this element in place, so Stimulus never re-runs connect(). Re-apply the bound
   // selection on the morph event too, but only the value-restoring call - registering the
   // window click listener a second time would leak a listener on every refused save.
   handleMorphElement = (event: Event): void => {
      if (event.target !== this.element) return;
      this.#writeOptionChromeHiddenSelectAndLabel();
      // A morph copies the server's attributes onto kept rows, and the server renders no `hidden`.
      if (this.searchValue) {
         this.#applySearch();
      }
   }

   // The page edited the options list (Turbo Stream replace, append, remove): re-apply the option
   // chrome and the current search to the rows it holds now, and refresh the closed label. Only
   // childList/characterData are observed, so the `hidden` writes of the search cannot re-trigger it.
   #onOptionsDomMutated = (): void => {
      // A page refresh is taking the control apart; nothing left to keep in step.
      if (!this.hasSelectInputTarget) return;

      this.#applyOptionChrome();
      this.#applySearch();
      this.#updateSelectInputAndValueDisplay();
   }

   connect() {
      // Make sure the dropdown closes when someone clicks outside the panel.
      window.addEventListener("click", this.onWindowClick);
      // Only a floating popup follows its trigger, so only it listens for resize and scroll.
      if (this.floatingValue) {
         window.addEventListener("resize", this.placePopup);
         window.addEventListener("scroll", this.placePopup, true);
      }
      this.element.addEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
      this.optionsObserver = new MutationObserver(this.#onOptionsDomMutated);
      this.optionsObserver.observe(this.optionsTarget, { childList: true, subtree: true, characterData: true });
      this.#writeOptionChromeHiddenSelectAndLabel();
      this.connected = true;
   }

   disconnect() {
      // Clean up the global event listener.
      window.removeEventListener("click", this.onWindowClick);
      window.removeEventListener("resize", this.placePopup);
      window.removeEventListener("scroll", this.placePopup, true);
      this.element.removeEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
      this.optionsObserver?.disconnect();
      this.optionsObserver = undefined;
      this.connected = false;
      this.searchDebouncer.cancel();

      // A search means nothing once the options may change: the next connection starts empty.
      if (this.searchValue) {
         this.searchValue = "";
      }
      // Stimulus already reports every target as absent here when a page refresh removed data-controller.
      const searchInput = this.element.querySelector<HTMLInputElement>("[data-searchable-select-target~='searchInput']");
      if (searchInput) {
         searchInput.value = "";
      }
      // The value reset above never reaches searchValueChanged during a disconnect.
      for (const row of this.optionElements) {
         row.removeAttribute("hidden");
      }
   }

   // ------------------
   // Stimulus value callbacks
   // ------------------
   selectedValueChanged(newValue, oldValue) {
      this.#writeOptionChromeHiddenSelectAndLabel();
   }

   searchValueChanged(newValue, oldValue) {
      this.#applySearch();
   }

   // ----------------
   // Stimulus Actions
   // ----------------
   onItemClick(event) {
      if (this.selectionModeValue === "Disabled") {
         return;
      }

      if (this.selectionModeValue === "Multiple") {
         this.#toggleSelected(event.currentTarget);
         // Don't close the dropdown in multiple mode to allow selecting more items
      } else {
         this.#select(event.currentTarget);
         this.close();
      }
   }

   onItemCheckboxClicked(event) {
      setTimeout(() => { // Hack to make sure this code runs after the browser has updated the checkbox state. If you don't do this, the browser unchecks the checkbox after the code checks it.
         var closestLi = event.target.closest("li");
         this.#toggleSelected(closestLi);
      }, 0)

   }

   onSearchInput() {
      this.searchDebouncer.run(this.debounceTimeMsValue, () => {
         this.searchValue = this.searchInputTarget.value
      });
   }

   // A fresh arrow on add/removeEventListener would leak the listener.
   onWindowClick = (event) => {
      const clickedElement = event.target;
      if (!this.element.contains(clickedElement)) {
         this.close();
      }
   }

   // A floating popup is `position: fixed`, so an `overflow` ancestor (a scroller, a modal) neither
   // clips it nor grows to hold it; the fixed box gets its place from the trigger. An ancestor holding
   // `transform`, `filter` or `contain: layout|paint` becomes its containing block and misplaces it
   // (`container-type` does not; .agents/refs/page-layout-clipping.md). Runs on open and again on
   // every resize and scroll, since a fixed box stays put while the trigger moves under it. A scroll
   // inside the popup (its own options list) moves nothing.
   placePopup = (event?: Event): void => {
      if (!this.floatingValue || !this.isOpen) return;
      if (event?.target instanceof Node && this.dialogTarget.contains(event.target)) return;

      const trigger = this.dropDownButtonTarget.getBoundingClientRect();
      const style = this.dialogTarget.style;
      style.position = "fixed";
      style.top = `${trigger.bottom}px`;
      style.left = `${trigger.left}px`;
      style.minWidth = `${trigger.width}px`;
   }

   onKeyDown(e) {
      // Enter and Space on a real button (the trigger, or the clear ×) must reach native
      // activation. Swallowing them here advertised tab stops a keyboard user could not honour.
      if ((e.key === "Enter" || e.key === " ") && (
         (this.hasClearButtonTarget && this.clearButtonTarget.contains(e.target))
         || (this.hasDropDownButtonTarget && this.dropDownButtonTarget.contains(e.target))
      )) {
         return;
      }

      // only preventDefault at keys we accept.  We don't want to block 'Tab'.
      switch (e.key) {
         case "ArrowUp":
            this.previous();
            e.preventDefault();
            break;
         case "ArrowDown":
            this.next();
            e.preventDefault();
            break;
         case "Escape":
            this.close();
            e.preventDefault();
            break;
         case "Enter":
         case " ":
            if (this.selectionModeValue === "Multiple" && this.isOpen) {
               // Space in the search field is a typed character (person names have spaces).
               if (e.key === " " && this.hasSearchInputTarget && this.searchInputTarget.contains(e.target)) {
                  return;
               }

               this.#toggleHighlighted(e);
               e.preventDefault();
               break;
            }

            this.close();
            e.preventDefault();
            break;
      }
   }

   disable() {
      this.dropDownButtonTarget.setAttribute("disabled", "");
   }

   enable() {
      this.dropDownButtonTarget.removeAttribute("disabled");
   }

   toggle() {
      if (this.isOpen) {
         this.close();
      } else {
         this.open();
      }
   }

   open() {
      if (this.selectionModeValue === "Disabled" || this.isOpen)
         return;

      this.dialogTarget.show();
      this.placePopup();
      this.#applyOptionChrome();

      if (this.selectionModeValue === "Multiple") {
         const visibleRows = this.visibleOptionElements;
         const initial = visibleRows.find((row) => row.getAttribute("data-selected") === "true")
            ?? visibleRows[0];
         if (initial) {
            this.#setActive(initial);
         }
      }

      this.#scrollToCurrentOption();

      if (this.hasSearchInputTarget) {
         this.searchInputTarget.focus();
      }
   }

   close() {
      if (this.isOpen) {
         this.dialogTarget.close();
      }
   }

   clear(event) {
      event.stopPropagation();

      if (this.hasSearchInputTarget) {
         this.searchInputTarget.value = "";
      }

      this.#deselectAll();
      this.close();
   }

   previous() {
      this.#moveHighlight(-1);
   }

   next() {
      this.#moveHighlight(1);
   }

   // ---------------
   // Private methods
   // ---------------

   // happy-dom sometimes never delivers selectedValueChanged after the data-* write.
   #writeSelectedValue(next: string[]) {
      this.selectedValue = next;
      this.#writeOptionChromeHiddenSelectAndLabel();
   }

   #applySearch() {
      const query = this.searchValue;
      const selected = this.selectedValue;
      for (const row of this.optionElements) {
         const hidden = !rowMatchesSearch(row, query, selected);
         row.toggleAttribute("hidden", hidden);
         if (hidden) {
            // A row the member cannot see must not stay the keyboard target.
            row.removeAttribute("data-active");
         }
      }
   }

   #applyOptionChrome() {
      for (const optionElement of this.optionElements) {
         const checkboxElement = optionElement.querySelector("input[type='checkbox']") as HTMLInputElement | null;
         const optionValue = optionElement.getAttribute('data-value');
         const isSelected = this.selectedValue.includes(optionValue);

         if (isSelected) {
            optionElement.setAttribute("data-selected", "true");
            if (checkboxElement) {
               checkboxElement.checked = true;
               checkboxElement.setAttribute("checked", "");
            }
         } else {
            optionElement.removeAttribute("data-selected");
            if (checkboxElement) {
               checkboxElement.checked = false;
               checkboxElement.removeAttribute("checked");
            }
         }
      }
   }

   #writeOptionChromeHiddenSelectAndLabel() {
      // A page refresh is taking the control apart; nothing left to keep in step.
      if (!this.hasSelectInputTarget) return;

      const fingerprint = JSON.stringify(this.selectedValue);
      const selectionUnchanged = fingerprint === this.lastAppliedSelectedValueJson;
      this.lastAppliedSelectedValueJson = fingerprint;

      this.#applyOptionChrome();
      this.#updateSelectInputAndValueDisplay();

      if (this.connected === true && !selectionUnchanged) {
         this.#dispatchChangeEvent();
      }
   }

   #deselectAll() {
      this.#writeSelectedValue([]);
   }

   #toggleSelected(element) {
      const value = element.getAttribute("data-value");
      const isSelected = this.selectedValue.includes(value);
      if (isSelected) {
         // Use filter to remove the value (splice returns removed elements, not the remaining array)
         this.#writeSelectedValue(this.selectedValue.filter(v => v !== value));
      } else {
         this.#writeSelectedValue(this.selectedValue.concat(value));
      }
   }

   #select(element) {
      const value = element.getAttribute("data-value");

      if (value === null) {
         return;
      }

      this.#writeSelectedValue([value]);
   }

   #deselect(element) {
      const value = element.getAttribute("data-value");

      if (!value)
         return;

      if (this.selectionModeValue === "Single" && this.selectedValue[0] === value) {
         this.#writeSelectedValue([]);
      } else if (this.selectedValue.includes(value)) {
         this.#writeSelectedValue(this.selectedValue.slice(this.selectedValue.indexOf(value), 1));
      }
   }

   #updateSelectInputAndValueDisplay() {
      const value = this.selectedValue
      
      // Clear existing options from the hidden select
      this.selectInputTarget.innerHTML = '';

      const selectedLabels: string[] = [];
      if (value && value.length > 0) {
         // Every row, hidden ones included: a selected row's label is read whether or not the search shows it.
         const optionElements = this.optionElements;
         for (const item of value) {
            const element = optionElements.find((el) => el.getAttribute("data-value") === item);
            if (element) {
               this.#appendSelectedOption(item);
               selectedLabels.push(labelOfRow(element).trim());
            }
         }
      } else if (this.hasClearButtonTarget) {
         // Not hasNullValue: Libraries/src/mvdmio.ASP.Razor/Pages/Components/SearchableSelect/SearchableSelect.cshtml
         // always renders the null-value attribute, as "" without a null value, while it renders the clear control only
         // with one. An empty null value still posts an empty value, where no option at all would post no key.
         this.#appendSelectedOption(this.nullValue);
      }

      // Same rule as SearchableSelectComponent.LooksEmpty.
      if (selectedLabels.length === 0) {
         this.selectedValueTextTarget.innerText = this.placeholderValue;
         this.selectedValueTextTarget.setAttribute('data-empty', '');
         this.selectedValueTextTarget.removeAttribute('title');
         return;
      }

      const displayText = this.selectionModeValue === "Single"
         ? selectedLabels[0]
         : selectedLabels.join(', ');
      this.selectedValueTextTarget.innerText = displayText;
      this.selectedValueTextTarget.setAttribute('title', displayText);
      this.selectedValueTextTarget.removeAttribute('data-empty');
   }

   #appendSelectedOption(value: string) {
      const optionElement = document.createElement('option');
      optionElement.value = value;
      optionElement.selected = true;
      this.selectInputTarget.appendChild(optionElement);
   }

   #moveHighlight(delta) {
      const options = this.visibleOptionElements;
      if (options.length === 0) {
         return;
      }

      const current = this.optionsTarget.querySelector("[data-active='true']")
         ?? this.optionsTarget.querySelector("[data-selected='true']");
      const currentIndex = current ? options.indexOf(current) : -1;
      const newIndex = Math.max(0, Math.min(currentIndex + delta, options.length - 1));

      if (newIndex === currentIndex) {
         return;
      }

      const next = options[newIndex];
      if (this.selectionModeValue === "Multiple") {
         this.#setActive(next);
      } else {
         this.#select(next);
      }

      this.#scrollToCurrentOption();
   }

   #toggleHighlighted(event) {
      const fromEvent = event.target instanceof Element
         ? event.target.closest("li[data-value]")
         : null;
      const option = fromEvent
         ?? this.optionsTarget.querySelector("[data-active='true']")
         ?? this.optionsTarget.querySelector("[data-selected='true']")
         ?? this.visibleOptionElements[0];

      if (!option) {
         return;
      }

      this.#setActive(option);
      this.#toggleSelected(option);
   }

   #setActive(element) {
      for (const option of this.optionElements) {
         option.removeAttribute("data-active");
      }

      element.setAttribute("data-active", "true");
   }

   #scrollToCurrentOption() {
      const current = this.optionsTarget.querySelector("[data-active='true']")
         ?? this.optionsTarget.querySelector("[data-selected='true']");
      if (current) {
         current.scrollIntoView({block: "center"});
      }
   }

   #dispatchChangeEvent() {
      this.selectInputTarget.dispatchEvent(new Event('change', {bubbles: true}));
   }
}
